/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable react/no-danger */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import {
  COLOR_SCHEMES,
  DARK_THEME_MEDIA_QUERY,
  DEFAULT_SYSTEM_THEME,
} from 'lib/next-themes/constants';
import type { ThemeProviderProps } from 'lib/next-themes/types';
import type { ReactElement } from 'react';
import { memo } from 'react';

interface Props extends ThemeProviderProps {
  readonly themeValues: string[];
  readonly defaultTheme: string;
}

const ThemeScript = memo<Props>(
  function ThemeScript({
    forcedTheme,
    storageKey,
    attribute,
    enableSystem,
    enableColorScheme,
    defaultTheme,
    value,
    themeValues,
    nonce,
  }: Props): ReactElement {
    const defaultSystem = defaultTheme === DEFAULT_SYSTEM_THEME;

    // Code-golfing the amount of characters in the script
    const optimization = ((): string => {
      if (attribute === 'class') {
        const removeClasses = `c.remove(${themeValues
          .map((t: string) => `'${t}'`)
          .join(',')})`;

        return `var d=document.documentElement,c=d.classList;${removeClasses};`;
      } else {
        return `var d=document.documentElement,n='${attribute}',s='setAttribute';`;
      }
    })();

    const fallbackColorScheme = ((): string => {
      if (typeof enableColorScheme === 'undefined' || !enableColorScheme) {
        return '';
      }

      const fallback =
        COLOR_SCHEMES.includes(defaultTheme) === true ? defaultTheme : null;

      if (fallback !== null) {
        return `if(e==='light'||e==='dark'||!e)d.style.colorScheme=e||'${defaultTheme}'`;
      } else {
        return `if(e==='light'||e==='dark')d.style.colorScheme=e`;
      }
    })();

    const updateDOM = (
      name: string,
      literal = false,
      setColorScheme = true,
    ): string => {
      const resolvedName: string | undefined =
        typeof value !== 'undefined' ? value[name] : name;
      const val = literal ? name + `|| ''` : `'${resolvedName}'`;
      let text = '';

      // MUCH faster to set colorScheme alongside HTML attribute/class
      // as it only incurs 1 style recalculation rather than 2
      // This can save over 250ms of work for pages with big DOM
      if (
        typeof enableColorScheme === 'boolean' &&
        enableColorScheme &&
        setColorScheme &&
        !literal &&
        COLOR_SCHEMES.includes(name) === true
      ) {
        text += `d.style.colorScheme = '${name}';`;
      }

      if (attribute === 'class') {
        if (literal || resolvedName) {
          text += `c.add(${val})`;
        } else {
          text += `null`;
        }
      } else {
        if (resolvedName) {
          text += `d[s](n,${val})`;
        }
      }

      return text;
    };

    const scriptSrc = ((): string => {
      if (typeof forcedTheme === 'string') {
        return `!function(){${optimization}${updateDOM(forcedTheme)}}()`;
      }

      if (typeof enableSystem === 'boolean' && enableSystem) {
        return `!function(){try{${optimization}var e=localStorage.getItem('${storageKey}');if('system'===e||(!e&&${defaultSystem})){var t='${DARK_THEME_MEDIA_QUERY}',m=window.matchMedia(t);if(m.media!==t||m.matches){${updateDOM(
          'dark',
        )}}else{${updateDOM('light')}}}else if(e){${
          value ? `var x=${JSON.stringify(value)};` : ''
        }${updateDOM(value ? `x[e]` : 'e', true)}}${
          !defaultSystem
            ? `else{` + updateDOM(defaultTheme, false, false) + '}'
            : ''
        }${fallbackColorScheme}}catch(e){}}()`;
      }

      return `!function(){try{${optimization}var e=localStorage.getItem('${storageKey}');if(e){${
        value ? `var x=${JSON.stringify(value)};` : ''
      }${updateDOM(value ? `x[e]` : 'e', true)}}else{${updateDOM(
        defaultTheme,
        false,
        false,
      )};}${fallbackColorScheme}}catch(t){}}();`;
    })();

    return (
      <script dangerouslySetInnerHTML={{ __html: scriptSrc }} nonce={nonce} />
    );
  },
  // Never re-render this component
  () => true,
);

export default ThemeScript;
